import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Flyout from 'Components/UI/Flyout';
import closeModal from 'Helpers/ModalHelper';
import relationships from 'Consts/Relationships';
import useCollection from 'Hooks/useCollection';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import useResource from 'Hooks/useResource';
import GoalSubsectionFieldlist from 'Components/Universal/Questionnaire/Sections/GoalSubsectionFieldlist';
import { goalResourcesToRecords, removeGoal, saveGoal } from 'Helpers/Goals';
import { Typography } from '@material-ui/core';
import PredefinedTaskSetMultiPicklist from 'Components/UI/PredefinedTaskSetMultiPicklist';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import CancelIcon from '@material-ui/icons/Cancel';
import { displayBestPractices as displayBestPracticesQuery } from 'Consts/Queries';
import { addQuery, QueryVal, removeQuery } from 'Helpers/Query';
import fr from 'facet-rest';
import { householdActionItemModal } from 'Consts/advisorUris';
import history from 'State/history';
import PlaybookToolDisplay from './PlaybookToolDisplay';
import PlaybookPlanDisplay from './PlaybookPlanDisplay';

const styles = theme => ({
  root: {
    width: '697px',
  },
  bestPracticesContainer: {
  },
  addActionItemContainer: {
    marginTop: theme.spacing(1),
  },
  sectionTitle: {
    marginTop: theme.spacing(3),
  },
  containerRoot: {
    padding: 0,
  },
  bestPracticesButton: {
    fontWeight: 'bold',
  },
  selectedBestPracticesButton: {
    backgroundColor: theme.palette.facet.primaryGray,
    '&:hover': {
      backgroundColor: theme.palette.facet.primaryGray,
    },
  },
  flyoutContainer: {
    position: 'absolute',
    width: '100%',
    height: '100%',
  },
  bestPracticesDisplay: {
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    backgroundColor: theme.palette.common.white,
    width: 'calc(100% - 697px)',
    height: '100%',
    zIndex: 1200,
  },
  flyoutBackdropOverride: {
    width: 0,
    '& > .MuiBackdrop-root': {
      display: 'none',
    },
  },
  bestPracticesFrame: {
    flex: 1,
    border: 0,
  },
  closeButton: {
    position: 'absolute',
    marginRight: 'auto',
  },
});

const checkAllCollectionsAreEmpty = (...args) => args.every(coll => coll.resources().list().length === 0);

const PlaybooksModal = ({ classes }) => {
  const [displayBestPractices, setDisplayBestPractices] = useState(QueryVal(displayBestPracticesQuery) === 'true');
  const [noPlaybooks, setNoPlaybooks] = useState(false);
  const { resource: playbookResource, initializeResource: initializePlaybookResource } = useResource();
  const { collection: goalCollection, setCollection: setGoalCollection } = useCollection();
  const { collection: toolsCollection, setCollection: setToolsCollection } = useCollection();
  const { collection: plansCollection, setCollection: setPlansCollection } = useCollection();
  const { collection: predefinedTaskSetCollection, setCollection: setPredefinedTaskSetCollection } = useCollection();
  const { householdResource, householdId } = useSimpleHousehold();
  const goalResourceList = goalCollection?.resources().list() || [];

  const toggleDisplayBestPractices = () => setDisplayBestPractices((current) => {
    const shouldDisplay = !current;
    if (shouldDisplay) {
      addQuery({ [displayBestPracticesQuery]: true });
    } else {
      removeQuery(displayBestPracticesQuery);
    }
    return shouldDisplay;
  });

  const openActionItemsModal = () => {
    history.push(householdActionItemModal(householdId));
  };


  const fetchData = async () => {
    if (QueryVal('playbookLink')) {
      const link = atob(QueryVal('playbookLink'));
      const playbookCollection = await fr.http.get(link);
      initializePlaybookResource(playbookCollection.resources().head());
    } else if (householdResource) {
      const playbookCollection = await householdResource.relationship(relationships.playbooks)
        .link('related')
        .Get();

      const playbook = playbookCollection.resources().head();
      initializePlaybookResource(playbook);
    }
  };

  useEffect(() => {
    fetchData();
  }, [householdResource]);

  useEffect(() => {
    if (playbookResource) {
      const goalCollectionAttr = playbookResource.attr('goals');
      const toolCollectionAttr = playbookResource.attr('tools');
      const plansCollectionAttr = playbookResource.attr('plans');
      const predefinedTaskSetCollectionAttr = playbookResource.attr('predefinedTaskSets');

      setNoPlaybooks(checkAllCollectionsAreEmpty(
        goalCollectionAttr,
        toolCollectionAttr,
        plansCollectionAttr,
        predefinedTaskSetCollectionAttr.resources().head().attr('predefinedTasks'),
      ));

      setGoalCollection(goalCollectionAttr);
      setToolsCollection(toolCollectionAttr);
      setPlansCollection(plansCollectionAttr);
      setPredefinedTaskSetCollection(predefinedTaskSetCollectionAttr);
    }
  }, [playbookResource]);

  return (
    <div className={classes.flyoutContainer}>
      <Flyout
        classes={{
          root: displayBestPractices && classes.flyoutBackdropOverride,
          paper: classes.root,
        }}
        open
        anchor="right"
        onClose={() => {
          closeModal();
          removeQuery('playbookLink');
        }}
      >
        <div className={classes.bestPracticesContainer}>
          <Button
            variant="contained"
            color="secondary"
            className={classNames(classes.bestPracticesButton, {
              [classes.selectedBestPracticesButton]: displayBestPractices,
            })}
            onClick={toggleDisplayBestPractices}
            disableElevation
          >
            View our Best Practices
          </Button>
        </div>
        {noPlaybooks && (
          <div className={classes.addActionItemContainer}>
            <Button
              variant="contained"
              color="secondary"
              className={classNames(classes.bestPracticesButton)}
              onClick={openActionItemsModal}
              disableElevation
            >
            Add Action Item
            </Button>
          </div>
        )}
        {predefinedTaskSetCollection && (
          <PredefinedTaskSetMultiPicklist
            predefinedTaskSet={predefinedTaskSetCollection.resources().head()}
          />
        )}
        {goalResourceList.length >= 1 && (
          <>
            <Typography
              variant="h2"
              color="primary"
              className={classes.sectionTitle}
            >
            Goals
            </Typography>
            <GoalSubsectionFieldlist
              disableAddButton
              planPrompt="View Plan"
              householdId={Number(householdId)}
              loadedGoals={goalResourcesToRecords(goalResourceList)}
              removeGoal={goalId => removeGoal(goalId, goalResourceList)}
              saveGoal={goal => saveGoal(goal, setGoalCollection, fetchData)}
            />
          </>
        )}
        {plansCollection?.resources().list().length >= 1 && (
          <>
            <Typography
              variant="h2"
              color="primary"
              className={classes.sectionTitle}
            >
            Plans
            </Typography>
            <PlaybookPlanDisplay
              plansCollection={plansCollection}
              fetchData={fetchData}
              householdId={householdId}
            />
          </>
        )}
        {toolsCollection?.resources().list().length >= 1 && (
          <>
            <Typography
              variant="h2"
              color="primary"
              className={classes.sectionTitle}
            >
            Tools
            </Typography>
            <PlaybookToolDisplay
              toolsCollection={toolsCollection}
            />
          </>
        )}
      </Flyout>
      {displayBestPractices && (
        <div className={classes.bestPracticesDisplay}>
          <IconButton
            color="primary"
            className={classes.closeButton}
            onClick={toggleDisplayBestPractices}
          >
            <CancelIcon />
          </IconButton>
          <iframe
            title="Best Practices"
            className={classes.bestPracticesFrame}
            src="https://docs.google.com/document/d/e/2PACX-1vTmRmzvbNswlppOQHvMoe2eopMNVvpoLlg9lmIvnTVjFSZlj4bqmJBGyXLE_F845jQKAuMOU6xyQmqT/pub?embedded=true"
          />
        </div>
      )}
    </div>
  );
};

PlaybooksModal.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(PlaybooksModal);
